import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Loader2, Mail, Send, History, HelpCircle, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog, Field } from "@/components/admin/Fields";
import { adminBookingEmailPreview, adminBookingEmailResolve, adminBookingEmailSend, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const KINDS = ["admin_notice", "confirmation", "decision"];

/** state → [badge class, label]. «accepted» is deliberately not called «levert»: the provider took it, delivery is not documented. */
export const OUTCOME = {
  not_sent: ["badge-muted", "Ikkje send"],
  skipped: ["badge-muted", "Ikkje send"],
  accepted: ["badge-confirmed", "Akseptert av leverandør"],
  queued: ["badge-muted", "I kø til kl. 08:00"],
  confirmed: ["badge-confirmed", "Stadfesta motteke"],
  failed: ["badge-cancelled", "Feila"],
  unknown: ["badge-warning", "Uvisst utfall"],
  sending: ["badge-reserved", "Sender …"],
  blocked: ["badge-warning", "Stoppa"],
  not_requested: ["badge-muted", "Ikkje send"],
};

export const outcomeText = (r) => {
  if (!r) return "";
  const [, label] = OUTCOME[r.outcome] || OUTCOME.not_sent;
  if (r.outcome === "accepted") return `${label} – ikkje stadfesta levert`;
  if (r.outcome === "confirmed") return `${label}${r.note ? ` – ${r.note}` : ""}`;
  return r.error ? `${label}: ${r.error}` : label;
};

const PORTAL_REASON = {
  no_event: null, no_tickets: "Arrangementet har ikkje billettar via huset – ingen arrangørlenke.", organizer_mismatch: "Arrangøren på arrangementet er ein annan enn på førespurnaden – ingen arrangørlenke.",
  organizer_missing: "Arrangøren finst ikkje lenger – ingen arrangørlenke.", organizer_email_differs: "Arrangørsida høyrer til ei anna e-postadresse – lenka vert ikkje send til innsendaren.",
  no_token: "Arrangøren har ikkje fått lenke enno – send henne frå Billettar → Arrangørar.", no_base_url: "Offentleg adresse manglar (Innstillingar) – arrangørlenkja kan ikkje lagast.",
};

const SettingsLink = () => <Link to="/admin/innstillingar" className="u-link font-medium" data-testid="booking-email-settings-link">Innstillingar → E-postvarsling</Link>;

const Attempt = ({ a }) => (
  <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px] text-muted-foreground" data-testid="booking-email-attempt">
    <span className="tabular">{a.at ? fmtDateTime(a.at) : "–"}</span>
    <span className={cn("badge-status", (OUTCOME[a.outcome] || OUTCOME.not_sent)[0])}>{(OUTCOME[a.outcome] || OUTCOME.not_sent)[1]}</span>
    {a.trigger === "manual" && <span>manuelt{a.by ? ` · ${a.by}` : ""}{a.replays ? " · same leverandørnøkkel som førre forsøk" : ""}</span>}
    {a.trigger === "resolution" && <span>avklart{a.by ? ` · ${a.by}` : ""}</span>}
    {a.provider_id && <span className="font-mono text-[11px]">id {a.provider_id}</span>}
    {a.error && <span className="basis-full text-foreground/80">{a.error}</span>}
    {a.note && !a.error && <span className="basis-full text-foreground/80">{a.note}</span>}
  </li>
);

/** The board's answer after talking to the recipient – the only way out of «uvisst utfall» without a provider replay. */
const ResolveDialog = ({ open, onOpenChange, entry, onResolve, loading }) => {
  const [note, setNote] = useState("");
  const ok = note.trim().length >= 5;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card" data-testid="booking-email-resolve-dialog">
        <DialogHeader><DialogTitle className="font-display">Avklar uvisst utfall</DialogTitle>
          <DialogDescription>Leverandøren svara ikkje, så vi veit ikkje om e-posten {entry.last?.to ? `til ${entry.last.to}` : ""} kom fram. Ei ny sending kunne blitt ein dublett – spør mottakaren først, og skriv ned kva du fekk vite.</DialogDescription></DialogHeader>
        <Field label="Kva fekk du vite, og korleis?"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="T.d. «Ringde Kari 12.06 – ingenting i innboksen eller søppelposten»" className="bg-background" data-testid="booking-email-resolve-note" /></Field>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" className="bg-card" disabled={!ok || loading} onClick={() => onResolve("received", note.trim())} data-testid="booking-email-resolve-received">Mottakaren har fått han</Button>
          <Button disabled={!ok || loading} onClick={() => onResolve("not_received", note.trim())} data-testid="booking-email-resolve-not-received">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kom ikkje fram – opne for ny sending"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ bookingId, kind, entry, onChanged, currentEmail }) => {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(false);
  const [ack, setAck] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const keyRef = useRef(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-booking", bookingId] }); qc.invalidateQueries({ queryKey: ["admin-bookings"] }); onChanged?.(); };
  const pv = useQuery({ queryKey: ["booking-email-preview", bookingId, kind], queryFn: () => adminBookingEmailPreview(bookingId, kind), enabled: preview, staleTime: 30_000, retry: false });
  const send = useMutation({
    mutationFn: (acknowledge) => { keyRef.current = keyRef.current || crypto.randomUUID(); return adminBookingEmailSend(bookingId, kind, { key: keyRef.current, acknowledge_duplicate_risk: !!acknowledge }); },
    onSuccess: (d) => {
      keyRef.current = null; setAck(false);
      const a = d.attempt || {};
      if (a.duplicate) toast.info("Same sending var alt registrert – ingen ny e-post gjekk ut");
      else if (a.outcome === "accepted") toast.success(`E-posten er akseptert av leverandøren (til ${a.to})`, { description: a.replays ? "Leverandøren brukte same nøkkel som førre forsøk – det finst berre éin e-post." : "Levering vert ikkje stadfesta automatisk – spør mottakaren om han kom fram." });
      else if (a.outcome === "queued") toast.success("Nattestopp: e-posten er lagd i kø og går kl. 08:00", { description: "Innhaldet vert laga då, ut frå førespurnaden slik han er om morgonen." });
      else if (a.outcome === "skipped") toast.warning(`Ikkje send: ${a.error}`);
      else if (a.outcome === "blocked") toast.error(`Stoppa: ${a.error}`);
      else toast.error(outcomeText(a));
      refresh();
    },
    onError: (e) => {
      keyRef.current = null;
      const d = e?.response?.data?.detail;
      if (d?.code === "needs_ack") setAck(true);
      else if (d?.code === "unknown_outcome") { toast.warning(d.message); setResolveOpen(true); }
      else if (d?.code === "in_progress") toast.warning(d.message);
      else toast.error(errorMessage(e, "Kunne ikkje sende"));
      refresh();
    },
  });
  const resolve = useMutation({
    mutationFn: ({ outcome, note }) => adminBookingEmailResolve(bookingId, kind, { outcome, note }),
    onSuccess: (d) => { setResolveOpen(false); toast.success(d.attempt?.outcome === "confirmed" ? "Registrert: mottakaren har fått e-posten" : "Registrert som ikkje motteken – du kan no sende på nytt"); refresh(); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikkje registrere avklaringa")),
  });
  const [cls, label] = OUTCOME[entry.sending ? "sending" : entry.state] || OUTCOME.not_sent;
  const last = entry.last;
  const unknown = entry.state === "unknown";
  const copy = (entry.state === "accepted" || entry.state === "confirmed") && !entry.to_changed;
  return (
    <li className="px-4 py-3" data-testid={`booking-email-${kind}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="min-w-0 flex-1 text-sm font-medium">{entry.label}{last?.to ? <span className="block truncate text-[12px] font-normal text-muted-foreground">til {last.to}</span> : null}</p>
        <span className={cn("badge-status", cls)} data-testid={`booking-email-${kind}-state`}>{label}</span>
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setPreview(true)} data-testid={`booking-email-${kind}-preview`}><Eye className="h-3.5 w-3.5" /> Sjå</Button>
        {unknown && !entry.idempotent_retry ? (
          <Button size="sm" className="h-8" onClick={() => setResolveOpen(true)} disabled={entry.sending} data-testid={`booking-email-${kind}-resolve`}><HelpCircle className="h-3.5 w-3.5" /> Avklar</Button>
        ) : (
          <Button size="sm" variant={entry.retry_safe || entry.idempotent_retry ? "default" : "outline"} className={cn("h-8", copy && "bg-card")}
            onClick={() => (copy ? setAck(true) : send.mutate(false))} disabled={send.isPending || entry.sending} data-testid={`booking-email-${kind}-send`}>
            {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : entry.idempotent_retry ? <RefreshCw className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            {entry.idempotent_retry ? "Prøv trygt på nytt" : copy ? "Send kopi" : entry.to_changed ? "Send til ny adresse" : "Send"}
          </Button>
        )}
      </div>
      {last && (
        <p className="mt-1.5 text-[12.5px] text-muted-foreground" data-testid={`booking-email-${kind}-last`}>
          {last.at ? `${fmtDateTime(last.at)} · ` : ""}{outcomeText(last)}{last.legacy ? " (registrert før loggen)" : ""}
          {last.portal?.reason && PORTAL_REASON[last.portal.reason] && last.outcome !== "blocked" ? <span className="block">{PORTAL_REASON[last.portal.reason]}</span> : null}
        </p>
      )}
      {entry.to_changed && (
        <p className="mt-2 flex items-start gap-2 rounded-lg bg-secondary px-3 py-2 text-[12.5px]" data-testid={`booking-email-${kind}-to-changed`}>
          <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>Adressa er retta etter siste sending: «Send til ny adresse» går til <strong>{currentEmail}</strong>. Førre gjekk til {last?.to}.</span>
        </p>
      )}
      {entry.state === "blocked" && (
        <p className="mt-2 flex items-start gap-2 rounded-lg bg-[hsl(var(--status-pending-bg))] px-3 py-2 text-[12.5px]" data-testid={`booking-email-${kind}-blocked`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-warning" /><span>Svaret lovar ei arrangørlenke som ikkje kan lagast utan offentleg adresse. Set adressa under <SettingsLink /> og trykk «Send» – ingenting er sendt.</span>
        </p>
      )}
      {unknown && (
        <p className="mt-2 flex items-start gap-2 rounded-lg bg-[hsl(var(--status-pending-bg))] px-3 py-2 text-[12.5px]" data-testid={`booking-email-${kind}-unknown-hint`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-warning" />
          <span>{entry.idempotent_retry ? "Leverandøren (Resend) kan køyre same sending om att med same nøkkel – då finst det garantert berre éin e-post. Ingen ny nøkkel vert brukt." : "Vi veit ikkje om e-posten gjekk. Ei ny sending vert ikkje tillaten før du har avklart med mottakaren og registrert svaret."}</span>
        </p>
      )}
      {entry.attempts?.length > 1 && (
        <button type="button" className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground u-link" onClick={() => setHistory((v) => !v)} data-testid={`booking-email-${kind}-history`}><History className="h-3 w-3" /> {history ? "Skjul" : "Vis"} historikk ({entry.attempts.length})</button>
      )}
      {history && <ul className="mt-2 space-y-1.5 border-l-2 border-border pl-3">{[...entry.attempts].reverse().map((a, i) => <Attempt key={`${a.at}-${i}`} a={a} />)}</ul>}

      <ConfirmDialog open={ack} onOpenChange={setAck} title="Sende ein kopi til?" confirmLabel="Ja, send kopi" loading={send.isPending} onConfirm={() => send.mutate(true)} testId={`booking-email-${kind}-ack-dialog`}
        text={entry.state === "confirmed" ? "Mottakaren har stadfesta at e-posten kom fram. Ein kopi gjev han den same e-posten ein gong til." : "Leverandøren har alt akseptert denne e-posten. Sender du ein kopi, får mottakaren han truleg to gonger. Gjer det berre om mottakaren seier at han ikkje kom fram."} />
      {resolveOpen && <ResolveDialog open={resolveOpen} onOpenChange={setResolveOpen} entry={entry} loading={resolve.isPending} onResolve={(outcome, note) => resolve.mutate({ outcome, note })} />}

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-2xl" data-testid={`booking-email-${kind}-preview-dialog`}>
          <DialogHeader className="space-y-1 border-b border-border/70 px-5 pb-4 pt-5 text-left">
            <DialogTitle className="font-display text-lg tracking-tight">{entry.label}</DialogTitle>
            <DialogDescription>{pv.data ? <>Til <strong>{pv.data.to}</strong> · «{pv.data.subject}»{pv.data.would_send ? "" : " · E-postvarsling er av – vert ikkje send automatisk"}</> : "Slik ser e-posten ut akkurat no, med data frå denne førespurnaden."}</DialogDescription>
          </DialogHeader>
          {pv.data?.portal?.reason && PORTAL_REASON[pv.data.portal.reason] && <p className="bg-[hsl(var(--status-pending-bg))] px-5 py-2 text-[12.5px]" data-testid={`booking-email-${kind}-portal-note`}>{PORTAL_REASON[pv.data.portal.reason]}</p>}
          {pv.data && !pv.data.base_url && <p className="bg-[hsl(var(--status-pending-bg))] px-5 py-2 text-[12.5px]">Offentleg adresse manglar – e-posten går utan knappar/lenker. Set henne under <SettingsLink />.</p>}
          <div className="relative h-[70vh] bg-[#F2EEE6]">
            {pv.isLoading && <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
            {pv.isError && (
              <div className="absolute inset-0 grid place-items-center px-6 text-center text-sm" data-testid={`booking-email-${kind}-preview-error`}>
                {pv.error?.response?.data?.detail?.code === "no_base_url"
                  ? <span className="max-w-md">{pv.error.response.data.detail.message} <span className="block pt-2"><SettingsLink /></span></span>
                  : <span className="text-destructive">{errorMessage(pv.error, "Kunne ikkje lage førehandsvisinga")}</span>}
              </div>
            )}
            {pv.data && <iframe title="E-post" srcDoc={pv.data.html} sandbox="" className="h-full w-full border-0" data-testid={`booking-email-${kind}-frame`} />}
          </div>
        </DialogContent>
      </Dialog>
    </li>
  );
};

/** Every e-mail about one request: what was tried, what the provider said, and a careful «send again». */
export const BookingEmails = ({ booking, onChanged, notifyEnabled = true }) => {
  const emails = booking.emails || {};
  const decided = booking.status === "godkjend" || booking.status === "avvist";
  return (
    <div className="rounded-xl border border-border" data-testid="booking-emails">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <p className="flex items-center gap-2 text-sm font-medium"><Mail className="h-4 w-4 text-muted-foreground" /> E-postar om førespurnaden</p>
        <span className="text-[11.5px] text-muted-foreground">«Akseptert» = leverandøren tok imot, ikkje stadfesta levert</span>
      </div>
      {!notifyEnabled && <p className="border-b border-border bg-status-warning/10 px-4 py-2 text-[12.5px] text-foreground/80" data-testid="booking-emails-disabled-hint">E-postvarsling er av under Innstillingar – «Send» her vert berre registrert som «Ikkje send» til varslinga er slått på.</p>}
      <ul className="divide-y divide-border">
        {KINDS.filter((k) => k !== "decision" || decided).map((k) => emails[k] && <Row key={k} bookingId={booking.id} kind={k} entry={emails[k]} onChanged={onChanged} currentEmail={booking.email} />)}
        {!decided && <li className="px-4 py-3 text-[12.5px] text-muted-foreground" data-testid="booking-email-decision-pending">Svaret til innsendaren vert tilgjengeleg her når førespurnaden er godkjend eller avvist.</li>}
      </ul>
    </div>
  );
};
